import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { IconLoader2 } from '@tabler/icons-react'
import { ForumSeo } from '@/components/ForumSeo'
import { ThreadListCard } from '@/components/forum/ThreadListCard'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/cn'
import { apiFetch } from '@/lib/api'
import { useAuthLoading, useConfirmedGuest, useSessionUser } from '@/lib/auth'
import {
  forumCard,
  forumCardHeader,
  forumKicker,
  forumLink,
  forumMuted,
  forumMutedSm,
  forumSectionBar,
  forumTitleLg,
} from '@/lib/forumUi'
import type { ForumCategorySummary, ThreadListItem } from '@/lib/types'
import { forumPageTitle, truncateForMetaDescription } from '~shared/seo'

export function CategoryPage() {
  const { slug } = useParams<{ slug: string }>()
  const [params, setParams] = useSearchParams()
  const me = useSessionUser()
  const authLoading = useAuthLoading()
  const guest = useConfirmedGuest()

  const sort = (params.get('sort') as 'active' | 'new' | 'unanswered' | null) ?? 'active'

  const [categoryMeta, setCategoryMeta] = useState<ForumCategorySummary | null>(null)
  const [threads, setThreads] = useState<ThreadListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const qs = useMemo(() => {
    const p = new URLSearchParams()
    p.set('sort', sort)
    if (slug) p.set('category', slug)
    return p.toString()
  }, [sort, slug])

  useEffect(() => {
    let alive = true
    setLoading(true)

    void apiFetch<{ categories: ForumCategorySummary[] }>(`/api/categories`)
      .then(payload => {
        if (!alive) return
        const hit = payload.categories.find(c => c.slug === slug) ?? null
        setCategoryMeta(hit)
      })
      .catch(() => {})

    void apiFetch<{ threads: ThreadListItem[] }>(`/api/threads?${qs}`)
      .then(payload => {
        if (!alive) return
        setThreads(payload.threads ?? [])
        setError(null)
      })
      .catch(() => {
        if (!alive) return
        setError('Threads failed to load for this forum.')
      })
      .finally(() => {
        if (!alive) return
        setLoading(false)
      })

    return () => {
      alive = false
    }
  }, [qs, slug])

  return (
    <div className="space-y-6">
      {categoryMeta ? (
        <ForumSeo
          title={forumPageTitle(`${categoryMeta.name} forum`)}
          description={
            categoryMeta.description?.trim()?.length ?
              truncateForMetaDescription(categoryMeta.description.trim())
            : truncateForMetaDescription(
                `Browse ${categoryMeta.name} threads from North Kingstown RI neighbors: events, civic life, and local discussion on NK Our Town.`,
              )
          }
        />
      ) : null}
      <header className={forumCard}>
        <div className={forumCardHeader}>
          <p className={`${forumKicker} flex flex-wrap items-center gap-x-2 gap-y-1`}>
            <Link className={`${forumLink} font-semibold uppercase tracking-[0.18em]`} to="/discussions">
              All forums
            </Link>
            <span className="text-zinc-300" aria-hidden>
              /
            </span>
            <span aria-current="page" className="font-semibold text-zinc-500">
              {categoryMeta?.name ?? slug}
            </span>
          </p>

          <h1 className={forumTitleLg}>{categoryMeta?.name ?? 'Forum category'}</h1>
          <p className={`${forumMuted} mt-2`}>
            {categoryMeta?.description ?? 'Use a clear headline. Add citations for news threads.'}{' '}
            <Link className={forumLink} to="/guidelines">
              Rules
            </Link>
            .
          </p>
        </div>
      </header>

      <section aria-labelledby="sort-label" className={forumCard}>
        <div className={forumSectionBar}>
          <h2 id="sort-label" className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
            Sort threads
          </h2>

          <div role="radiogroup" aria-label="Category sort options" className="flex flex-wrap gap-2 rounded-xl bg-white p-1 ring-1 ring-zinc-200/80">
            {([
              ['active', 'Active'],
              ['new', 'New'],
              ['unanswered', 'Unanswered'],
            ] as const).map(([k, label]) => {
              const active = sort === k
              return (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={cn(
                    'rounded-lg px-3 py-2 text-[13px] font-semibold uppercase tracking-wide transition md:whitespace-nowrap',
                    active ? 'bg-zinc-900 text-white shadow-sm' : 'text-zinc-600 hover:bg-zinc-50',
                  )}
                  onClick={() => {
                    const next = new URLSearchParams(params)
                    next.set('sort', k)
                    setParams(next)
                  }}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>

        <div role="feed" aria-label="Forum threads">
          <div aria-live={loading ? 'polite' : 'off'} aria-busy={loading}>
            {loading ? (
              <div className="flex items-center gap-2 px-5 py-4 text-[14px] font-medium text-zinc-600">
                <IconLoader2 className="size-5 shrink-0 animate-spin" aria-hidden="true" />
                Loading threads…
              </div>
            ) : null}
          </div>

          {error ? <p className="border-t border-red-100 bg-red-50 px-5 py-3 text-[14px] font-medium text-red-900">{error}</p> : null}

          {!loading && !error && threads.length === 0 ? (
            <div className="px-5 py-12 md:px-8">
              <h3 className="text-xl font-semibold text-zinc-950">Nothing posted here yet</h3>
              <p className={`mt-2 max-w-[62ch] ${forumMutedSm} text-[15px] text-zinc-600`}>
                {authLoading
                  ? 'Checking your session…'
                  : me
                    ? 'Start the board off with a clear title and the details neighbors need.'
                    : 'Use “New topic” in the toolbar once you’re logged in.'}
              </p>
              <div className={`mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-[14px] font-semibold uppercase tracking-wide ${forumMutedSm}`}>
                <Link className={forumLink} to="/discussions">
                  Browse all boards
                </Link>
                <span className="text-zinc-300">•</span>
                {authLoading ? (
                  <span className="inline-flex items-center gap-1.5 font-medium normal-case text-zinc-500">
                    <IconLoader2 className="size-4 shrink-0 animate-spin" aria-hidden="true" />
                    Session…
                  </span>
                ) : me ? (
                  <Link className="no-underline" to="/new">
                    <Button size="sm" className="rounded-lg bg-sky-600 font-semibold text-white hover:bg-sky-500">
                      New topic
                    </Button>
                  </Link>
                ) : (
                  <Link className={forumLink} to="/sign-in">
                    Sign in
                  </Link>
                )}
                <span className="text-zinc-300">•</span>
                <Link className={forumLink} to="/guidelines">
                  Guidelines
                </Link>
              </div>
            </div>
          ) : (
            threads.map((t, idx) => <ThreadListCard key={t.id} thread={t} isLastRow={idx === threads.length - 1} />)
          )}
        </div>
      </section>
    </div>
  )
}
